import { I18n } from '@calendly/i18n'
import { omit, partition, pick } from 'lodash-es'

import { invitationToEventTypeMembership, organizationMembershipToEventTypeMembership } from 'components/event_type_edit'

export const eventTypeMembershipToSuggestion = (membership) => {
  switch (membership.type) {
    case 'OrganizationMembership':
      return {
        id: `organization_membership-${membership.organization_membership_id}`,
        type: 'organization_membership',
        entity: {
          id: membership.organization_membership_id,
          ...pick(membership, 'user_id', 'name', 'email', 'avatar_url', 'avatar_letter',
            'is_zoom_configured', 'is_gotomeeting_configured', 'is_microsoft_teams_configured',
            'is_webex_configured', 'google_conferencing_configured', 'user_uuid')
        }
      }
    case 'OrganizationInvitation':
      return {
        id: `organization_invitation-${membership.organization_invitation_id}`,
        type: 'organization_invitation',
        entity: {
          id: membership.organization_invitation_id,
          uuid: membership.organization_invitation_uuid,
          status: 'pending',
          ...pick(membership, 'email', 'avatar_letter')
        }
      }
    default:
      return null
  }
}


export const suggestionToEventTypeMembership = (suggestion) => {
  switch (suggestion.type) {
    case 'organization_membership':
      return organizationMembershipToEventTypeMembership(suggestion.entity)
    case 'organization_invitation':
      return invitationToEventTypeMembership(suggestion.entity)
    default:
      return null
  }
}

export const isSameMembership = (membership1, membership2) => {
  if (!membership1 || !membership2) return false
  if (membership1.type !== membership2.type) return false

  switch (membership1.type) {
    case 'OrganizationMembership':
      return membership1.organization_membership_id === membership2.organization_membership_id
    case 'OrganizationInvitation':
      return membership1.organization_invitation_id === membership2.organization_invitation_id
    default:
      return false
  }
}

const findMembership = (memberships, membershipToFind) => (
  memberships.find((membership) => isSameMembership(membership, membershipToFind))
)

const filterOutMembership = (memberships, membershipToRemove) => (
  memberships.filter((membership) => !isSameMembership(membership, membershipToRemove))
)

export const updateMemberships = (existingMemberships, newMemberships, {
  userPoolUid = null,
  addPriority = false,
  markForDestruction = true
} = {}) => {
  const [existingMembersToUpdate, newMembers] = partition(newMemberships, (newMembership) => (
    findMembership(existingMemberships, newMembership)
  ))

  // Update existingMemberships - either mark them as enabled or mark for destruction
  const updatedMemberships = existingMemberships.map((membership) => {
    const existingMember = findMembership(existingMembersToUpdate, membership)

    // Preserve ID for existing memberships and mark as not destroyed and enabled
    if (existingMember) {
      return {
        ...omit(membership, '_destroy'),
        ...omit(existingMember, '_destroy'),
        ...(userPoolUid ? { user_pool_uid: userPoolUid } : {}),
        enabled: true
      }
    }

    // Keep memberships from other pools unchanged
    if (userPoolUid && membership.user_pool_uid !== userPoolUid) {
      return membership
    }

    // This membership is not in the new list but has an ID - mark for destruction
    if (membership.id || !markForDestruction) {
      return { ...membership, _destroy: markForDestruction, enabled: false }
    }

    // Filter out memberships without IDs that are not in the new list
    return null
  }).filter(Boolean)

  // Then add new members
  return [
    ...updatedMemberships,
    ...newMembers.map((newMember) => ({
      ...newMember,
      ...(userPoolUid ? { user_pool_uid: userPoolUid } : {}),
      ...(addPriority ? { priority: 0 } : {}),
      enabled: true
    }))
  ]
}

export const removeMembership = ({ memberships, eventTypeMembership, markForDestruction = true }) => {
  const existingMember = findMembership(memberships, eventTypeMembership)

  if (existingMember?.id || !markForDestruction) {
    return memberships.map((membership) => (
      isSameMembership(membership, eventTypeMembership)
        ? { ...membership, ...eventTypeMembership, _destroy: markForDestruction, enabled: false }
        : membership
    ))
  }

  return filterOutMembership(memberships, eventTypeMembership)
}

export const getUserIds = (memberships) => memberships.filter(
  ({ type }) => type === 'OrganizationMembership'
).map(({ user_id: userId }) => userId)

export const getInvitationIds = (memberships) => memberships.filter(
  ({ type }) => type === 'OrganizationInvitation'
).map(({ organization_invitation_id: organizationInvitationId }) => organizationInvitationId)

export const getUserPoolUid = (userPool) => (userPool?.uuid || userPool?.key)

export const withoutDeleted = (objects) => objects.filter(({ _destroy }) => !_destroy)

export const filterByPoolUid = (memberships, userPoolUid) => memberships.filter(({ user_pool_uid: uid }) => uid === userPoolUid)

export const filterOutByPoolUid = (memberships, userPoolUid) => memberships.filter(({ user_pool_uid: uid }) => uid !== userPoolUid)

export const filterByPoolingType = (userPools, poolingType) => userPools
  .filter((userPool) => userPool.pooling_type === poolingType)

export const removeUserPoolUids = (memberships) => memberships.map((membership) => omit(membership, ['user_pool_uid']))

export const findUserPool = (userPools, userPoolUid) => userPools.find((pool) => getUserPoolUid(pool) === userPoolUid)

export const getPrepareHostOptionProps = ({
  memberships,
  isTeam,
  isCollective,
  isRoundRobin,
  isCollectiveRoundRobin,
  isAddingRotatingHosts,
  userPoolUid,
  collectiveUserPoolUid
}) => (eventTypeMembership) => {
  const isAddingCoHosts = !isAddingRotatingHosts
  const existingMembership = findMembership(memberships, eventTypeMembership)
  const membershipUserPoolUid = existingMembership?.user_pool_uid

  const markedForDestruction = existingMembership?._destroy === true
  const fromSamePool = membershipUserPoolUid === userPoolUid
  const fromCollectivePool = membershipUserPoolUid === collectiveUserPoolUid

  if (existingMembership && !markedForDestruction) {
    if (
      (isAddingCoHosts && isCollective)
      || (isAddingRotatingHosts && isRoundRobin)
      || (isCollectiveRoundRobin && fromSamePool)
    ) {
      return {
        selected: isTeam ? existingMembership.enabled : true,
        disabled: false,
        disabledHint: null
      }
    }

    let disabledHint = null

    if (isAddingCoHosts) {
      if (isRoundRobin || (isCollectiveRoundRobin && !fromCollectivePool)) {
        disabledHint = I18n.t('publisher.event_type_editor.sections.hosts.picker.in_rotating_pool')
      }
    }

    if (isAddingRotatingHosts) {
      if (isCollective || (isCollectiveRoundRobin && fromCollectivePool)) {
        disabledHint = I18n.t('publisher.event_type_editor.sections.hosts.picker.already_co_host')
      }

      if (isCollectiveRoundRobin && !fromCollectivePool && !fromSamePool) {
        disabledHint = I18n.t('publisher.event_type_editor.sections.hosts.picker.in_another_pool')
      }
    }

    return {
      selected: false,
      disabled: true,
      disabledHint
    }
  }

  return {
    selected: false,
    disabled: false,
    disabledHint: null
  }
}

export const filterByNameOrEmail = (hosts, query) => hosts.filter(
  ({ name, email }) => {
    const loweredQuery = query.toLowerCase()

    return name.toLowerCase().includes(loweredQuery) || email.toLowerCase().includes(loweredQuery)
  }
)

export const getDefaultUserPoolName = (index = 0) => (
  I18n.t('publisher.event_type_editor.sections.hosts.round_robin.default_pool_name', { index: index ? index + 1 : '' })
)

export const getNextUserPoolName = (userPools) => {
  const maxIndex = userPools.reduce((max, pool) => {
    const match = pool.name.match(new RegExp(`^${getDefaultUserPoolName()}(\\d+)$`))
    if (match) {
      const index = parseInt(match[1], 10)
      return Math.max(max, index)
    }
    return max
  }, userPools.length)

  return getDefaultUserPoolName(maxIndex)
}

export const reassignUserPoolsPositions = (userPools) => {
  const collectiveUserPools = filterByPoolingType(userPools, 'collective')
  const roundRobinUserPools = filterByPoolingType(userPools, 'round_robin')

  return [
    ...collectiveUserPools.map((pool, index) => ({ ...pool, position: index })),
    ...roundRobinUserPools.map((pool, index) => ({
      ...pool,
      name: pool.name || getDefaultUserPoolName(index),
      position: collectiveUserPools.length + index
    }))
  ]
}

export const removeUserPool = (userPools, userPoolUid) => reassignUserPoolsPositions(
  userPools.map((pool) => {
    if (getUserPoolUid(pool) !== userPoolUid) return pool
    if (pool.uuid) return { ...pool, _destroy: true }
    return null
  }).filter(Boolean)
)
